import { ChemistryVideoError } from './chemistryVideoUtils';

export function getSupportedVideoMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ].find((type) => MediaRecorder.isTypeSupported(type));
}

export function recordChemistryCanvasVideo({ canvas, durationMs, fps = 30, renderFrame, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const mimeType = getSupportedVideoMimeType();
    if (!mimeType || !canvas?.captureStream || typeof MediaRecorder === 'undefined') {
      reject(new ChemistryVideoError('media_recorder_unsupported'));
      return;
    }
    if (!canvas.getContext('2d')) {
      reject(new ChemistryVideoError('canvas_context_missing'));
      return;
    }

    let stream;
    let recorder;
    let animationFrameId = 0;
    let chunks = [];
    let settled = false;
    let startedAt = 0;

    const cancelFrame = () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      animationFrameId = 0;
    };
    const stopTracks = () => stream?.getTracks().forEach((track) => track.stop());
    const removeAbortListener = () => signal?.removeEventListener('abort', handleAbort);
    const cleanup = () => {
      cancelFrame();
      stopTracks();
      removeAbortListener();
    };
    const fail = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      chunks = [];
      reject(error instanceof ChemistryVideoError || error?.name === 'AbortError'
        ? error
        : new ChemistryVideoError('media_recorder_failed', error));
    };
    const handleAbort = () => {
      cancelFrame();
      if (recorder?.state && recorder.state !== 'inactive') recorder.stop();
      else fail(new DOMException('Video generation aborted', 'AbortError'));
    };

    try {
      stream = canvas.captureStream(fps);
      recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 6_000_000 });
    } catch (error) {
      fail(new ChemistryVideoError('media_recorder_failed', error));
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) chunks.push(event.data);
    };
    recorder.onerror = (event) => fail(new ChemistryVideoError('media_recorder_failed', event.error || event));
    recorder.onstop = () => {
      if (settled) return;
      if (signal?.aborted) {
        fail(new DOMException('Video generation aborted', 'AbortError'));
        return;
      }
      const blob = new Blob(chunks, { type: recorder.mimeType || mimeType });
      chunks = [];
      if (!blob.size) {
        fail(new ChemistryVideoError('empty_video_blob'));
        return;
      }
      settled = true;
      cleanup();
      onProgress?.(100);
      resolve({ blob, mimeType: blob.type, durationMs });
    };

    const render = (timestamp) => {
      if (signal?.aborted || settled) return;
      const elapsedMs = Math.min(timestamp - startedAt, durationMs);
      try {
        renderFrame(canvas.getContext('2d'), elapsedMs);
      } catch (error) {
        if (recorder.state !== 'inactive') recorder.stop();
        fail(new ChemistryVideoError('render_failed', error));
        return;
      }
      onProgress?.(Math.min(99, Math.round((elapsedMs / durationMs) * 100)));
      if (elapsedMs >= durationMs) {
        if (recorder.state !== 'inactive') recorder.stop();
        return;
      }
      animationFrameId = requestAnimationFrame(render);
    };

    signal?.addEventListener('abort', handleAbort, { once: true });
    if (signal?.aborted) {
      handleAbort();
      return;
    }
    try {
      renderFrame(canvas.getContext('2d'), 0);
      recorder.start(250);
      startedAt = performance.now();
      animationFrameId = requestAnimationFrame(render);
    } catch (error) {
      fail(new ChemistryVideoError('media_recorder_failed', error));
    }
  });
}
